import { Badge, Divider, Group, PasswordInput, Text, Title } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useQuery } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'

// The slice of the host form this component reads and writes (same casting
// convention as OrganizationBridgeSection).
export type StediHostForm = UseFormReturnType<{
  stediApiKey: string
}>

interface OrganizationStediSectionProps {
  organizationId?: string
  form: StediHostForm
}

export function OrganizationStediSection({ organizationId, form }: OrganizationStediSectionProps) {
  const orpc = useOrpc()

  const { data: status } = useQuery(
    orpc.getOrganizationStediStatus.queryOptions({
      input: { organizationId: organizationId ?? '' },
      enabled: Boolean(organizationId),
    }),
  )

  const configured = status?.configured === true
  const environment = status?.environment

  return (
    <>
      <Divider my="sm" />
      <Group justify="space-between" mb="xs">
        <Title order={4}>Stedi (Clearinghouse)</Title>
        {/* There is no shared fallback key: each organization needs its own, so empty is
            "off", not "default". The mode is shown because it is the mistake that actually
            happens: a test key pasted into a production organization. */}
        {configured ? (
          <Badge color={environment === 'production' ? 'green' : 'yellow'} variant="light">
            {environment === 'production' ? 'Configured (Production)' : 'Configured (Test mode)'}
          </Badge>
        ) : (
          <Badge color="gray" variant="light">
            Not set up
          </Badge>
        )}
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        {configured
          ? 'A key is saved. Enter a new one below to replace it.'
          : 'This organization cannot use Stedi until an API key is saved here.'}
      </Text>

      <PasswordInput
        label="Stedi API key"
        placeholder="test_… or a live key"
        autoComplete="new-password"
        {...form.getInputProps('stediApiKey')}
      />
      <Text size="xs" c="dimmed">
        The key is <strong>checked against Stedi before it is saved</strong>, so a wrong one is
        refused here rather than surfacing later as a failed claim. Test keys (<code>test_…</code>)
        keep every transaction mock; a live key sends real traffic to payers — the badge above says
        which mode this organization is pointed at. The key is never shown again.
      </Text>
    </>
  )
}
